import { Company } from "@domain/CRM/company";
import { Grower } from "@domain/CRM/grower";
import { Job, SkipsEmailResult, SkipsResult, type MapFeatureEdit, type SkipsInExe } from "@domain/CRM/job";
import { Inflate, inflate } from "pako";
import { StandardAxiosConfig, blobErrorDetail, getBaseDataURL, parseDates } from "../../../common";
import { CreateAPI_Base, DataAPI_Base, GetAPI_Base, UpdateAPI_Base } from "../base";
import { postJobDropoffs, postJobLocations, postJobMapFeatureEdits, postJobOrders } from "./jobs.schedulingWrites";
import { apiClient } from "../../../client";
import { IAirtableAttachment } from "@domain/CRM/Attachment";
/**
 * Jobs create API — transport-only. The `many`/`one` methods are inherited
 * from `CreateAPI_Base` and make the raw HTTP POST. Callers that need the
 * full creation pipeline (associate growers, salespersons, branches, prep
 * payload) should use `submitJobs` / `submitJob` from
 * `domain/job/jobCreationService` instead of calling this class directly.
 */
class CreateAPI_Jobs extends CreateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}
}

class GetAPI_Jobs extends GetAPI_Base<Job> {
	constructor() {
		super("jobs", Job, true);
	}

	public async byCompany(company: Company, branchNames?: string[]): Promise<Job[]> {
		if (!company.name) return [];
		// Compressed (zlib) and lite: several thousand jobs are ~52MB raw JSON,
		// most of it attachment URL arrays. The lite payload omits attachments
		// (job.attachments === null); consumers load them on demand via
		// jobsList.ensureAttachments().
		const url = `${getBaseDataURL("jobs")}/get-by-company?compressed=true&include_attachments=false`;
		const body: { company_name: string; branch_names?: string[] } = { company_name: company.name };
		if (branchNames && branchNames.length > 0) body.branch_names = branchNames;
		const response = await apiClient.post(url, body, StandardAxiosConfig(0, { isCompressed: true }));
		if (response.status !== 200) {
			throw new Error(`Failed to get jobs for company`);
		}
		const decompressed = inflate(await (response.data as Blob).arrayBuffer(), { to: "string" });
		const records = JSON.parse(decompressed) as Job[];
		const jobs = records.map((j) => Object.assign(new Job(), j));
		parseDates<Job>(jobs);
		return jobs;
	}

	/**
	 * Retrieves `company`'s unready jobs linked to one of `growers`, matched by grower id (the names only narrow the query).
	 *
	 * @param growers - An array of `Grower` objects to filter unready jobs by. If empty or undefined, returns an empty array.
	 */
	public async byGrowers(company: Company, growers: Grower[]): Promise<Job[]> {
		if (!growers || growers.length === 0) return [];
		const url = `${getBaseDataURL("jobs")}/get-unready-for-grower`;
		const ids = growers.map((g) => g.id);
		const body = { company_name: company.name, grower_ids: ids, grower_names: growers.map((g) => g.name) };
		const response = await apiClient.post<Job[]>(url, body, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unready jobs for growers`);
		}
		const jobs = response.data.map((j) => Object.assign(new Job(), j));
		parseDates<Job>(jobs);
		return jobs;
	}

	public async unscheduled(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/get-unscheduled`;
		const response = await apiClient.post<Job[]>(url, {}, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unscheduled jobs`);
		}
		const jobs = response.data.map((j) => Object.assign(new Job(), j));
		parseDates<Job>(jobs);
		return jobs;
	}

	/**
	 * Streaming variant of `byCompany`: the server sends zlib-compressed NDJSON,
	 * one line per batch of (attachment-less) jobs, flushed as Airtable pages
	 * arrive. `onBatch` fires per line so callers can render progressively;
	 * resolves with the full list once the stream ends.
	 */
	public async byCompanyStream(
		company: Company,
		branchNames: string[] | undefined,
		onBatch: (jobs: Job[]) => void,
	): Promise<Job[]> {
		if (!company.name) return [];
		const body: { company_name: string; branch_names?: string[] } = { company_name: company.name };
		if (branchNames && branchNames.length > 0) body.branch_names = branchNames;
		return this.streamJobs(`${getBaseDataURL("jobs")}/get-by-company/stream`, body, onBatch);
	}

	/**
	 * Streaming variant of `unscheduled` for the scheduling map: lite jobs plus the inline
	 * boundary and map-feature texts, batched as the server fetches pages. `attachments` is null
	 * when the API judged the inline boundary text usable (it parses, with at least one feature).
	 */
	public async unscheduledStream(onBatch: (jobs: Job[]) => void): Promise<Job[]> {
		return this.streamJobs(`${getBaseDataURL("jobs")}/get-unscheduled/stream`, {}, onBatch);
	}

	/**
	 * The scheduling board's scheduled jobs: every job on a shift that is NOT sampled (no "Sample
	 * Date"), filtered Airtable-side on the job's own fields — no shift list needed, and every
	 * shift's jobs, so the caller narrows to the shifts it shows. `unscheduledStream`'s payload,
	 * with the boundary shapefile beside the GeoJSON attachment on the jobs that carry them.
	 *
	 * One compressed list through `apiClient`, exactly like the by-ids `POST /jobs/get` it
	 * replaced — never a stream: a partly loaded set would read as "the rest are sampled", and a
	 * failure has to reach the interceptor as it did (toast, Sentry, session-expiry logout).
	 */
	public async scheduledUnsampled(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/get-scheduled-unsampled`;
		const response = await apiClient.post(url, {}, StandardAxiosConfig(0, { isCompressed: true }));
		const decompressed = inflate(await (response.data as Blob).arrayBuffer(), { to: "string" });
		// A body that does not inflate whole yields no string, and the parse throws: the read
		// fails rather than resolve with the jobs that did arrive.
		const records = JSON.parse(decompressed) as Job[];
		const jobs = records.map((j) => Object.assign(new Job(), j));
		parseDates<Job>(jobs);
		return jobs;
	}

	/**
	 * POST to a jobs streaming endpoint (zlib-compressed NDJSON; one line per
	 * batch) and surface each batch via `onBatch`; resolves with the full list.
	 *
	 * Uses `fetch` (axios can't stream response bodies in the browser), so the
	 * apiClient interceptors don't apply — auth headers come from
	 * StandardAxiosConfig directly.
	 */
	private async streamJobs(url: string, body: unknown, onBatch: (jobs: Job[]) => void): Promise<Job[]> {
		const config = StandardAxiosConfig();
		const response = await fetch(url, {
			method: "POST",
			headers: { ...(config.headers as Record<string, string>), "Content-Type": "application/json" },
			body: JSON.stringify(body),
		});
		if (!response.ok || !response.body) {
			throw new Error(`Failed to stream jobs`);
		}

		const all: Job[] = [];
		let buffer = "";
		const handleText = (text: string) => {
			buffer += text;
			let newline: number;
			while ((newline = buffer.indexOf("\n")) >= 0) {
				const line = buffer.slice(0, newline);
				buffer = buffer.slice(newline + 1);
				if (!line.trim()) continue;
				const records = JSON.parse(line) as Job[];
				const jobs = records.map((j) => Object.assign(new Job(), j));
				parseDates<Job>(jobs);
				all.push(...jobs);
				onBatch(jobs);
			}
		};

		const inflator = new Inflate({ to: "string" });
		// `to: "string"` makes pako emit string chunks; its types stay Uint8Array | string.
		inflator.onData = (chunk) => handleText(chunk as unknown as string);

		const reader = response.body.getReader();
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			inflator.push(value, false);
			if (inflator.err) throw new Error(`Failed to decompress jobs stream: ${inflator.msg}`);
		}
		inflator.push(new Uint8Array(0), true);
		if (inflator.err) throw new Error(`Failed to decompress jobs stream: ${inflator.msg}`);
		return all;
	}

	public async attachmentsByIds(ids: string[]): Promise<Record<string, Job["attachments"]>> {
		if (ids.length === 0) return {};
		const url = `${getBaseDataURL("jobs")}/get-attachments`;
		const response = await apiClient.post<Record<string, Job["attachments"]>>(
			url,
			{ job_ids: ids },
			StandardAxiosConfig(),
		);
		if (response.status !== 200) {
			throw new Error(`Failed to get job attachments`);
		}
		return response.data;
	}

	// `fresh` bypasses the day-long metadata cache behind this endpoint (a separate
	// `Cache` the generic data-router's `force_refresh` can't reach; M9: `?fresh=1`).
	public async lastCropOptions(fresh?: boolean): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/last-crop${fresh ? "?fresh=1" : ""}`;
		const response = await apiClient.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get last crop options`);
		} else {
			return response.data;
		}
	}

	public async nextCropOptions(fresh?: boolean): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/next-crop${fresh ? "?fresh=1" : ""}`;
		const response = await apiClient.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get next crop options`);
		} else {
			return response.data;
		}
	}
}

class UpdateAPI_Jobs extends UpdateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}

	/**
	 * Update a single field on a single Job. `field` is a `keyof Job` (e.g.
	 * `"dealIds"`, `"branchIds"`); the BE translates this to the underlying
	 * Airtable column. `value` must be in the shape Airtable expects: `["recXXX"]`
	 * for a linked-record field, a string for text, etc.
	 *
	 * Use this instead of `many([fullJob])` when you've loaded a Job via a lite
	 * payload (matcher pages, etc.) — `many` round-trips every field, which would
	 * clear any column not present in the lite payload.
	 *
	 * The route writes only its allowlist (`_WRITABLE_FIELDS` in `routers/data/jobs.py`:
	 * `dealIds`, `branchIds`, and `mgrNotes` / `csvSentToLab` for ROGO users) — anything else is a 400, so a
	 * new PATCH here needs the field added there first.
	 */
	async field<K extends keyof Job>(id: string, field: K, value: unknown): Promise<Job> {
		const url = `${getBaseDataURL("jobs")}/update/${id}/${field}`;
		// Serialized here rather than left to axios. Axios only infers `application/json` for
		// objects and arrays; hand it a bare string or number and it sends
		// `application/x-www-form-urlencoded` with the raw value as the body. FastAPI's `Body()`
		// then parses nothing out of it and the route writes an empty value — a 200 that saved
		// NOTHING. Every caller until now passed a linked-record array, which is why the bug sat
		// here unseen; the Skips queue's notes column is the first plain-string write.
		const config = StandardAxiosConfig();
		(config.headers as Record<string, string>)["Content-Type"] = "application/json";
		const response = await apiClient.post<Job>(url, JSON.stringify(value), config);
		if (response.status !== 200) {
			throw new Error(`Failed to update '${String(field)}' on job ${id}: ${response.statusText}`);
		}
		const updated = Object.assign(new Job(), response.data);
		parseDates<Job>([updated]);
		return updated;
	}

	/**
	 * Recalculate the job's status (central-nervous-system) and resolve to the refreshed job.
	 *
	 * The calculator runs itself off a queue and a five-minute schedule, so this only asks it
	 * sooner. The API waits for it and reads the job back, so the resolved Job already carries the
	 * new status — do NOT refetch instead, that races the calculator's write.
	 */
	async recalculateStatus(id: string): Promise<Job> {
		const url = `${getBaseDataURL("jobs")}/recalculate-status/${encodeURIComponent(id)}`;
		// The standard config's default is no client timeout, which is what this wants: the API
		// bounds the wait at 300s and answers 502 past that, so the deadline stays on one side.
		const response = await apiClient.post<Job>(url, undefined, StandardAxiosConfig());
		if (response.status !== 200) throw new Error(`Failed to recalculate the status of job ${id}`);
		const updated = Object.assign(new Job(), response.data);
		parseDates<Job>([updated]);
		return updated;
	}

	/** Scheduling's four batch partial-writes. Bodies live in `jobs.schedulingWrites.ts`,
	 *  mirroring the backend sub-router that serves them. */
	async orders(updates: { jobId: string; order: number | null }[]): Promise<void> {
		return postJobOrders(updates);
	}

	async dropoffs(updates: { jobId: string; shiftDropoffId: string | null }[]): Promise<void> {
		return postJobDropoffs(updates);
	}

	async locations(updates: { jobId: string; location: string | null }[]): Promise<void> {
		return postJobLocations(updates);
	}

	async mapFeatures(edits: MapFeatureEdit[]): Promise<void> {
		return postJobMapFeatureEdits(edits);
	}

	async files(id: string, fileList: File[]) {
		const url = `${getBaseDataURL("jobs")}/upload-file/${id}`;

		async function upload(file: File) {
			const formData = new FormData();
			formData.append("file", file);

			const response = await apiClient.post(url, formData, StandardAxiosConfig(10, { isFileUpload: true }));
			if (response.status !== 200) {
				throw new Error(`Failed to upload file: ${file.name}`);
			} else {
				return response.data;
			}
		}

		if (!fileList) return [];
		const fileArray = Array.from(fileList);
		const uploadResults = await Promise.all(
			fileArray.map(async (file) => {
				const s3url = await upload(file);
				return { url: s3url, filename: file.name } as IAirtableAttachment;
			}),
		);

		return uploadResults;
	}
}

class JobIssuesAPI extends DataAPI_Base<Job> {
	constructor() {
		super("jobs", Job);
	}

	/** GET one /jobs/issues/<path> list as Job instances; `failure` is the error thrown on a non-200. */
	private async getIssueJobs(path: string, failure: string): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/${path}`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) throw new Error(failure);
		const jobs = response.data.map((j) => Object.assign(new Job(), j));
		parseDates<Job>(jobs);
		return jobs;
	}

	getAgrian(): Promise<Job[]> {
		return this.getIssueJobs("agrian", "Failed to get Agrian issues");
	}

	async fixAgrian(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/agrian`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix Agrian issues`);
		} else {
			return response.data;
		}
	}

	getDataSendback(): Promise<Job[]> {
		return this.getIssueJobs("data-sendback", "Failed to get data sendback issues");
	}

	getDealMatcherJobs(): Promise<Job[]> {
		return this.getIssueJobs("deal-matcher", "Failed to get unmatched deal jobs");
	}

	getBranchMatcherJobs(): Promise<Job[]> {
		return this.getIssueJobs("branch-matcher", "Failed to get unmatched branch jobs");
	}

	async fixDealMatcherJobs(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/deal-matcher`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix unmatched deal jobs`);
		} else {
			return response.data;
		}
	}

	getExeMissionValidationJobs(): Promise<Job[]> {
		return this.getIssueJobs("exe-mission-validation", "Failed to get Exe Mission Validation jobs");
	}

	getGeoValidationJobs(): Promise<Job[]> {
		return this.getIssueJobs("geo-validation", "Failed to get Geo validation jobs");
	}

	getStatusIssueJobs(): Promise<Job[]> {
		return this.getIssueJobs("status", "Failed to get Status Calculator jobs");
	}

	getManualReviewJobs(): Promise<Job[]> {
		return this.getIssueJobs("manual-review", "Failed to get manual review jobs");
	}

	getMiscIssueJobs(): Promise<Job[]> {
		return this.getIssueJobs("misc", "Failed to get misc job errors");
	}

	getSkipsJobs(): Promise<Job[]> {
		return this.getIssueJobs("skips", "Failed to get skips");
	}

	/** The map the skips email carries, as PNG bytes — shown in the composer before Send. A failure's
	 *  `detail` arrives as a Blob under `responseType: "blob"`, so it is read back into the Error here,
	 *  where the VM can show "no executed mission" apart from "the map service is down". */
	async getSkipsMap(jobId: string): Promise<Blob> {
		const url = `${getBaseDataURL("jobs")}/issues/skips/${encodeURIComponent(jobId)}/map`;
		try {
			const response = await apiClient.get<Blob>(url, { ...StandardAxiosConfig(), responseType: "blob" });
			return response.data;
		} catch (err: any) {
			const detail = await blobErrorDetail(err);
			throw new Error(detail || err?.message || "Failed to load the skips map");
		}
	}

	/** Emails the customer about the job's skips (from the logged-in user, map inline) and flags the
	 *  job "Skips - Customer Contacted"; the result carries the flags as stored, or null with a
	 *  `flagError` when the mail went out but the flag write did not. A failure's server `detail`
	 *  (a 404 no-mission, a 422 rejected recipient, a 502 provider failure) becomes the Error's
	 *  message, the same translation its sibling `getSkipsMap` makes. */
	async sendSkipsEmail(
		jobId: string,
		email: { to: string[]; bcc: string[]; subject: string; body: string },
	): Promise<SkipsEmailResult> {
		const url = `${getBaseDataURL("jobs")}/issues/skips/${encodeURIComponent(jobId)}/email`;
		try {
			const response = await apiClient.post<SkipsEmailResult>(url, email, StandardAxiosConfig());
			return response.data;
		} catch (err: any) {
			const detail = await blobErrorDetail(err);
			throw new Error(detail || err?.message || "Failed to send the skips email");
		}
	}

	/** Runs the Skip Management flow for one job, via the pre-sampling-processor lambda.
	 *  Synchronous on the server and slow by design (it re-runs the exit interview): tens of
	 *  seconds. `staged` is what the operator changed on the row and is the request body as-is —
	 *  the server embeds each under exactly these names and saves the classification and bill to
	 *  Airtable before the run starts (the lambda branches on the classification), and Marked
	 *  Ready after it. A key left out, or undefined (JSON drops it), leaves the record's value
	 *  alone. */
	async processSkips(
		jobId: string,
		staged: { classification?: SkipsInExe; billablePercent?: number; markedReady?: boolean } = {},
	): Promise<SkipsResult> {
		const url = `${getBaseDataURL("jobs")}/issues/skips/${encodeURIComponent(jobId)}`;
		const response = await apiClient.post<SkipsResult>(url, staged, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to process skips`);
		}
		return response.data;
	}

	/** Approve a job's manual review: the API adds the "Manual Review Approved" flag, then re-runs the exit
	 *  interview and answers once it has finished. Resolves to the job's flags. */
	async approveManualReview(jobId: string): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/issues/manual-review/${encodeURIComponent(jobId)}`;
		const response = await apiClient.post<string[]>(url, undefined, StandardAxiosConfig());
		if (response.status !== 200) throw new Error("Failed to approve the manual review");
		return response.data;
	}

	/** Re-process a job's geo data (the pre-sampling processor's process-geo flow); resolves once it has finished.
	 *  `skipValidation` runs it without the geo-spatial validation, which clears the job's validation errors. */
	async processGeo(jobId: string, { skipValidation = false }: { skipValidation?: boolean } = {}): Promise<void> {
		const skip = skipValidation ? "?skip=true" : "";
		const url = `${getBaseDataURL("jobs")}/issues/geo-validation/${encodeURIComponent(jobId)}${skip}`;
		const response = await apiClient.post(url, undefined, StandardAxiosConfig());
		if (response.status !== 200) throw new Error("Failed to re-process the geo data");
	}

	async fixExeMissionValidationJobs(ids: string[], force = false): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/exe-mission-validation${force ? "?force=true" : ""}`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix Exe Mission Validation jobs`);
		} else {
			return response.data;
		}
	}

	async fixDataSendback(ids: string[], forceEmail = false, forceApi = false): Promise<void> {
		const params = new URLSearchParams();
		if (forceEmail) params.set("force_email", "true");
		if (forceApi) params.set("force_api", "true");
		const qs = params.toString();
		const url = `${getBaseDataURL("jobs")}/issues/data-sendback${qs ? `?${qs}` : ""}`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix data sendback issues`);
		}
		return response.data;
	}
}
export class JobsAPI extends DataAPI_Base<Job> {
	get: GetAPI_Jobs;
	create: CreateAPI_Jobs;
	update: UpdateAPI_Jobs;
	issues: JobIssuesAPI;

	// job data is compressed, as it can get quite large
	constructor() {
		super("jobs", Job, true);
		this.get = new GetAPI_Jobs();
		this.create = new CreateAPI_Jobs();
		this.update = new UpdateAPI_Jobs();
		this.issues = new JobIssuesAPI();
	}
}
